// Design token constants — single source of truth for palette, radii, shadows, gradients.
// All components must consume from here; no hard-coded hex values elsewhere.

// --- Palette ---

export const PALETTE_LIGHT = {
  primary: {
    main: '#F26522',
    dark: '#C94E10',
    soft: '#FFE7D6',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#1B365D',
    contrastText: '#FFFFFF',
  },
  accent: {
    main: '#F7B32B',
  },
  success: {
    main: '#16A34A',
  },
  warning: {
    main: '#F59E0B',
  },
  error: {
    main: '#DC2626',
  },
  info: {
    main: '#0EA5E9',
  },
  background: {
    default: '#FAFAF7',
    paper: '#FFFFFF',
    elevated: '#FFFFFF',
    subtle: '#F4F2EE',
  },
  text: {
    primary: '#0F172A',
    secondary: '#475569',
    tertiary: '#94A3B8',
  },
  divider: 'rgba(15,23,42,0.08)',
};

export const PALETTE_DARK = {
  primary: {
    main: '#FF8A50',
    dark: '#F26522',
    soft: 'rgba(255,138,80,0.14)',
    contrastText: '#0F1F3D',
  },
  secondary: {
    main: '#7DA4D9',
    contrastText: '#0F172A',
  },
  accent: {
    main: '#FFCA5C',
  },
  success: {
    main: '#4ADE80',
  },
  warning: {
    main: '#F59E0B',
  },
  error: {
    main: '#F87171',
  },
  info: {
    main: '#38BDF8',
  },
  background: {
    default: '#0B1220',
    paper: '#121A2B',
    elevated: '#17223A',
    subtle: '#0E1626',
  },
  text: {
    primary: '#F1F5F9',
    secondary: '#A3B1C2',
    tertiary: '#6B7A8F',
  },
  divider: 'rgba(255,255,255,0.08)',
};

// --- Radii ---

export const RADIUS = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
};

// Librarian-portal-specific scale: tighter radii for a less-rounded, more
// utilitarian feel. Consumed only by files under pages/librarian/ and
// LibrarianPortal.js via `import { RADIUS_LIBRARIAN as RADIUS }`. Do not
// import this in any other portal — student/admin/sales/marketing/seo
// should keep the default RADIUS scale.
export const RADIUS_LIBRARIAN = {
  sm: 3,
  md: 4,
  lg: 6,
  xl: 8,
  pill: 999,
};

// --- Shadows ---

export const SHADOWS_LIGHT = {
  xs: '0 1px 2px rgba(15,23,42,0.04)',
  sm: '0 2px 6px rgba(15,23,42,0.06)',
  md: '0 8px 20px rgba(15,23,42,0.08)',
  lg: '0 16px 40px rgba(15,23,42,0.10)',
  glowPrimary: '0 8px 28px rgba(242,101,34,0.28)',
  glowAccent: '0 8px 28px rgba(247,179,43,0.32)',
};

export const SHADOWS_DARK = {
  xs: '0 1px 2px rgba(0,0,0,0.5)',
  sm: '0 2px 8px rgba(0,0,0,0.4)',
  md: '0 10px 24px rgba(0,0,0,0.45)',
  lg: '0 20px 48px rgba(0,0,0,0.55)',
  glowPrimary: '0 10px 32px rgba(255,138,80,0.28)',
  glowAccent: '0 10px 32px rgba(255,202,92,0.30)',
};

// --- Brand gradients (CSS string fragments, used inside linear-gradient()) ---

export const GRADIENTS = {
  brand: 'linear-gradient(135deg, #F26522 0%, #F7B32B 100%)',
  brandDeep: 'linear-gradient(135deg, #C94E10 0%, #F26522 55%, #F7B32B 100%)',
  coin: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
  surfaceLight: 'linear-gradient(160deg, #FFFFFF 0%, #FAFAF7 100%)',
  surfaceDark: 'linear-gradient(160deg, #17223A 0%, #121A2B 100%)',
  navyLight: 'linear-gradient(135deg, #1B365D 0%, #0F1F3D 100%)',
  navyDark: 'linear-gradient(135deg, #17223A 0%, #0B1220 100%)',
};

// --- Aurora gradients (auth screens) ---

export const AURORA_GRADIENTS = {
  auroraLightBase:
    'radial-gradient(ellipse at top left, #FFE7D6 0%, transparent 55%), radial-gradient(ellipse at bottom right, #DCE6F4 0%, transparent 55%), linear-gradient(180deg, #FAFAF7 0%, #F4F2EE 100%)',
  auroraDarkBase:
    'radial-gradient(ellipse at top left, rgba(242,101,34,0.18) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(27,54,93,0.45) 0%, transparent 55%), linear-gradient(180deg, #0B1220 0%, #0E1626 100%)',
  auroraOrbAmber:
    'radial-gradient(circle at 30% 30%, #F7B32B 0%, rgba(247,179,43,0) 70%)',
  auroraOrbOrange:
    'radial-gradient(circle at 30% 30%, #F26522 0%, rgba(242,101,34,0) 70%)',
  auroraOrbNavyLight:
    'radial-gradient(circle at 30% 30%, #1B365D 0%, rgba(27,54,93,0) 70%)',
  auroraOrbNavyDark:
    'radial-gradient(circle at 30% 30%, #7DA4D9 0%, rgba(125,164,217,0) 70%)',
};

// --- Icon sizes (px) ---

export const ICON_SIZES = {
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};
